import { useEffect, useState, useCallback } from "react";
import { Smartphone, Share, PlusSquare, Download, X } from "lucide-react";

const DISMISS_KEY = "jcc-shiftkit-install-dismissed";

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
}

function detectIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIOSUA = /iPad|iPhone|iPod/.test(ua);
  const isIPadOS = ua.includes("Macintosh") && typeof document !== "undefined" && "ontouchend" in document;
  return isIOSUA || isIPadOS;
}

export default function InstallShiftKit() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    setInstalled(isStandalone());
    setIsIOS(detectIOS());
    try {
      if (window.localStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    } catch { /* ignore */ }

    // Pick up an event that was already captured before mount
    if (window.__jccDeferredInstallPrompt) {
      setDeferredPrompt(window.__jccDeferredInstallPrompt);
    }

    const onCustom = () => {
      if (window.__jccDeferredInstallPrompt) {
        setDeferredPrompt(window.__jccDeferredInstallPrompt);
      }
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("jcc:beforeinstallprompt", onCustom);
    window.addEventListener("jcc:appinstalled", onInstalled);
    return () => {
      window.removeEventListener("jcc:beforeinstallprompt", onCustom);
      window.removeEventListener("jcc:appinstalled", onInstalled);
    };
  }, []);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice && choice.outcome === "accepted") setInstalled(true);
      } catch { /* user cancelled or unsupported */ }
      window.__jccDeferredInstallPrompt = null;
      setDeferredPrompt(null);
      return;
    }
    if (isIOS) setShowIOSHelp((v) => !v);
  }, [deferredPrompt, isIOS]);

  if (installed || dismissed) return null;

  const showAndroidInstall = !!deferredPrompt;
  const showIOSCard = isIOS && !deferredPrompt;
  const showFallback = !showAndroidInstall && !showIOSCard;

  return (
    <div
      data-testid="install-shiftkit-card"
      className="rounded-2xl border border-teal/20 bg-teal/5 p-4 sm:p-5 mb-8 flex items-start gap-4"
    >
      <div className="w-10 h-10 rounded-xl bg-white text-teal flex items-center justify-center shrink-0 shadow-sm">
        <Smartphone className="w-5 h-5" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display text-base sm:text-lg font-semibold text-ink">
            Add Shift Kit to your home screen
          </h3>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss install prompt"
            data-testid="install-shiftkit-dismiss"
            className="text-ink-muted hover:text-ink rounded-full p-1 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-1 text-sm text-ink-muted">
          Open Shift Kit like an app — one tap from your phone, straight to the tools you use on shift.
        </p>

        {showAndroidInstall && (
          <div className="mt-4">
            <button
              type="button"
              onClick={handleInstall}
              data-testid="install-shiftkit-android-button"
              className="btn-teal text-sm py-2.5"
            >
              <Download className="w-4 h-4" />
              Add Shift Kit to Home Screen
            </button>
          </div>
        )}

        {showIOSCard && (
          <div className="mt-4">
            <button
              type="button"
              onClick={handleInstall}
              data-testid="install-shiftkit-ios-button"
              className="btn-outline text-sm py-2.5"
              aria-expanded={showIOSHelp}
            >
              <Smartphone className="w-4 h-4" />
              {showIOSHelp ? "Hide instructions" : "How to add on iPhone / iPad"}
            </button>
            {showIOSHelp && (
              <ol
                data-testid="install-shiftkit-ios-instructions"
                className="mt-4 space-y-2 text-sm text-ink"
              >
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-white border border-teal/30 text-teal text-xs font-semibold flex items-center justify-center shrink-0">1</span>
                  <span className="flex-1">In Safari, tap the <Share className="inline w-4 h-4 mx-1 -mt-0.5" strokeWidth={1.75} /> <strong>Share</strong> button at the bottom of the screen.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-white border border-teal/30 text-teal text-xs font-semibold flex items-center justify-center shrink-0">2</span>
                  <span className="flex-1">Scroll and tap <PlusSquare className="inline w-4 h-4 mx-1 -mt-0.5" strokeWidth={1.75} /> <strong>Add to Home Screen</strong>.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-white border border-teal/30 text-teal text-xs font-semibold flex items-center justify-center shrink-0">3</span>
                  <span className="flex-1">Tap <strong>Add</strong> in the top-right corner. Shift Kit will open from your home screen.</span>
                </li>
              </ol>
            )}
          </div>
        )}

        {showFallback && (
          <p className="mt-3 text-xs text-ink-muted">
            Open this page on your phone (Chrome on Android or Safari on iPhone / iPad) to add Shift Kit to your home screen.
          </p>
        )}
      </div>
    </div>
  );
}
